<template>
  <div>
    <v-container fluid>
      <base-material-card
        icon="mdi-shield-crown"
        title="จัดการรายงานรอบข้อมูลบุคลากร (Admin)"
        class="px-5 py-3"
      >
        <!-- ============================================================ -->
        <!-- ส่วนที่ 1: Dashboard ภาพรวม                                  -->
        <!-- ============================================================ -->
        <v-card class="mb-5" flat>
          <div class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon left color="indigo">mdi-view-dashboard</v-icon>
            ภาพรวมระบบรายงาน
          </div>
          <v-row>
            <v-col cols="6" sm="4" md="2">
              <v-card color="indigo lighten-5" class="pa-3 text-center" flat rounded="lg">
                <div class="text-h5 font-weight-bold indigo--text">{{ overviewStats.total_colleges || 0 }}</div>
                <div class="text-caption indigo--text" style="line-height:1.2">สถานศึกษาทั้งหมด</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-card color="blue lighten-5" class="pa-3 text-center" flat rounded="lg">
                <div class="text-h5 font-weight-bold blue--text text--darken-2">{{ overviewStats.current_round_submissions || 0 }}</div>
                <div class="text-caption blue--text text--darken-1" style="line-height:1.2">รอบปัจจุบัน</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-card color="green lighten-5" class="pa-3 text-center" flat rounded="lg">
                <div class="text-h5 font-weight-bold green--text text--darken-2">{{ overviewStats.current_round_completed || 0 }}</div>
                <div class="text-caption green--text text--darken-1" style="line-height:1.2">เสร็จสิ้นแล้ว</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-card color="orange lighten-5" class="pa-3 text-center" flat rounded="lg">
                <div class="text-h5 font-weight-bold orange--text text--darken-3">{{ overviewStats.current_round_pending || 0 }}</div>
                <div class="text-caption orange--text text--darken-2" style="line-height:1.2">รอดำเนินการ</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-card color="purple lighten-5" class="pa-3 text-center" flat rounded="lg">
                <div class="text-h5 font-weight-bold purple--text">{{ Number(overviewStats.total_personnel_reported || 0).toLocaleString() }}</div>
                <div class="text-caption purple--text" style="line-height:1.2">บุคลากรรายงานแล้ว</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-card
                :color="activeRound ? 'teal lighten-5' : 'grey lighten-4'"
                class="pa-3 text-center"
                flat
                rounded="lg"
              >
                <div class="text-h5 font-weight-bold" :class="activeRound ? 'teal--text text--darken-2' : 'grey--text'">
                  {{ activeRound ? 'Q' + activeRound.round_number : '-' }}
                </div>
                <div class="text-caption" :class="activeRound ? 'teal--text text--darken-1' : 'grey--text'" style="line-height:1.2">
                  {{ activeRound ? 'เปิดอยู่' : 'ไม่มีรอบเปิด' }}
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card>

        <!-- ============================================================ -->
        <!-- ส่วนที่ 2: จัดการปีงบประมาณ + ปฏิทินรอบ                     -->
        <!-- ============================================================ -->
        <v-card class="mb-5" outlined>
          <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
            <v-icon left color="blue darken-2">mdi-calendar-multiple</v-icon>
            จัดการปีงบประมาณ และ รอบรายงาน
            <v-spacer></v-spacer>
            <v-btn color="primary" depressed small @click="openCreateCycleDialog()">
              <v-icon left small>mdi-plus-circle</v-icon>
              สร้างปีงบประมาณใหม่
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <!-- เลือกปีงบประมาณ -->
            <v-row align="center" class="mb-3">
              <v-col cols="12" md="4">
                <v-select
                  v-model="selectedCycleYear"
                  :items="cycleYearOptions"
                  label="เลือกปีงบประมาณ"
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-calendar"
                  @change="loadRounds()"
                ></v-select>
              </v-col>
              <v-col cols="12" md="8">
                <v-chip
                  v-if="activeRound"
                  color="teal"
                  dark
                  small
                  class="mr-2"
                >
                  <v-icon small left>mdi-broadcast</v-icon>
                  รอบที่เปิดอยู่: {{ activeRound.round_name }}
                </v-chip>
              </v-col>
            </v-row>

            <!-- ตารางปฏิทิน 4 รอบ -->
            <v-row v-if="rounds.length > 0">
              <v-col
                v-for="round in rounds"
                :key="round.round_id"
                cols="12"
                md="6"
                lg="3"
              >
                <v-card
                  outlined
                  :class="{
                    'round-card-open': round.round_status === 'open',
                    'round-card-closed': round.round_status === 'closed'
                  }"
                  class="round-card pa-4"
                >
                  <!-- Header -->
                  <div class="d-flex align-center justify-space-between mb-2">
                    <v-chip
                      x-small
                      :color="roundStatusColor(round.round_status)"
                      dark
                    >{{ roundStatusLabel(round.round_status) }}</v-chip>
                    <span class="text-caption grey--text">{{ round.round_quarter }}</span>
                  </div>
                  <div class="text-subtitle-2 font-weight-bold mb-1">{{ round.round_name }}</div>
                  <div class="text-caption grey--text mb-3" style="line-height:1.4">
                    ข้อมูลช่วง: {{ formatThaiDateShort(round.round_data_period_start) }} -
                    {{ formatThaiDateShort(round.round_data_period_end) }}
                  </div>

                  <!-- วันเปิด-ปิด -->
                  <v-simple-table dense class="mb-3">
                    <tbody>
                      <tr>
                        <td class="text-caption pa-1">เปิดรับ</td>
                        <td class="text-caption pa-1 font-weight-bold text-right">{{ formatThaiDateShort(round.round_start_date) }}</td>
                      </tr>
                      <tr>
                        <td class="text-caption pa-1">ปิดรับ</td>
                        <td class="text-caption pa-1 font-weight-bold text-right">{{ formatThaiDateShort(round.round_end_date) }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>

                  <!-- ปุ่มจัดการ -->
                  <div>
                    <v-btn
                      v-if="round.round_status === 'draft'"
                      color="teal"
                      dark
                      depressed
                      block
                      small
                      @click="confirmAction('open', round)"
                      :disabled="!!activeRound"
                    >
                      <v-icon left small>mdi-lock-open-variant</v-icon>เปิดรอบ
                    </v-btn>
                    <v-btn
                      v-if="round.round_status === 'open'"
                      color="red darken-1"
                      dark
                      depressed
                      block
                      small
                      @click="confirmAction('close', round)"
                    >
                      <v-icon left small>mdi-lock</v-icon>ปิดรอบ
                    </v-btn>
                    <v-btn
                      v-if="round.round_status === 'open' || round.round_status === 'closed'"
                      color="indigo"
                      dark
                      depressed
                      block
                      small
                      class="mt-2"
                      @click="viewRoundSubmissions(round)"
                    >
                      <v-icon left small>mdi-eye</v-icon>ดูรายงานสถานศึกษา
                    </v-btn>
                    <v-btn
                      v-if="round.round_status === 'draft'"
                      text
                      block
                      small
                      color="grey darken-1"
                      class="mt-2"
                      @click="openEditRoundDialog(round)"
                    >
                      <v-icon left small>mdi-pencil</v-icon>แก้ไขวันที่
                    </v-btn>
                  </div>
                </v-card>
              </v-col>
            </v-row>
            <v-alert v-else type="info" outlined dense class="mt-2">
              กรุณาเลือกปีงบประมาณ หรือสร้างปีงบประมาณใหม่
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- ============================================================ -->
        <!-- ส่วนที่ 3: สรุปสถานะสถานศึกษาของรอบที่เลือก (Donut + Table)-->
        <!-- ============================================================ -->
        <v-card v-if="selectedRound" class="mb-5" outlined>
          <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
            <v-icon left color="deep-purple">mdi-chart-arc</v-icon>
            ภาพรวมสถานศึกษา — {{ selectedRound.round_name }}
            <v-spacer></v-spacer>
            <v-chip small :color="roundStatusColor(selectedRound.round_status)" dark class="mr-2">
              {{ roundStatusLabel(selectedRound.round_status) }}
            </v-chip>
            <v-btn icon small @click="loadRoundSubmissions(selectedRound.round_id)" :loading="submissionsLoading">
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <!-- สรุปสถานะ (Summary Cards) -->
            <v-row class="mb-4">
              <v-col
                v-for="(st, idx) in summaryStatusCards"
                :key="idx"
                cols="6"
                sm="4"
                md="auto"
                class="flex-grow-1"
              >
                <v-card
                  :color="st.bg"
                  class="pa-3 text-center summary-status-card"
                  flat
                  rounded="lg"
                  :outlined="st.count > 0"
                  @click="filterByStatus(st.value)"
                  style="cursor:pointer"
                >
                  <div class="text-h6 font-weight-bold" :style="{color: st.numColor}">{{ st.count }}</div>
                  <div class="text-caption" :style="{color: st.labelColor}" style="line-height:1.2">{{ st.label }}</div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Progress Bar ภาพรวม -->
            <div class="mb-4">
              <div class="d-flex justify-space-between mb-1">
                <span class="text-caption font-weight-bold">
                  ความคืบหน้าภาพรวม
                </span>
                <span class="text-caption font-weight-bold" :class="overallProgressColor + '--text'">
                  {{ overallProgressPercent }}%
                </span>
              </div>
              <v-progress-linear
                :value="overallProgressPercent"
                :color="overallProgressColor"
                height="10"
                rounded
              ></v-progress-linear>
            </div>

            <!-- ค้นหา + ตัวกรอง -->
            <v-row align="center" class="mb-2">
              <v-col cols="12" md="5">
                <v-text-field
                  v-model="submissionSearch"
                  append-icon="mdi-magnify"
                  label="ค้นหาสถานศึกษา"
                  single-line
                  hide-details
                  dense
                  outlined
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="submissionFilter"
                  :items="statusFilterOptions"
                  label="กรองสถานะ"
                  dense
                  outlined
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-filter-variant"
                ></v-select>
              </v-col>
              <v-col cols="12" md="3" class="text-right">
                <v-btn
                  color="green darken-1"
                  dark
                  depressed
                  small
                  @click="approveAllUploaded()"
                  :disabled="uploadedCount === 0"
                >
                  <v-icon left small>mdi-check-all</v-icon>
                  อนุมัติทั้งหมดที่อัปโหลดแล้ว ({{ uploadedCount }})
                </v-btn>
              </v-col>
            </v-row>

            <!-- ตารางสถานศึกษา -->
            <v-data-table
              :headers="submissionHeaders"
              :items="filteredSubmissions"
              :search="submissionSearch"
              :items-per-page="20"
              :footer-props="{ 'items-per-page-options': [10, 20, 50, -1] }"
              :loading="submissionsLoading"
              dense
              class="elevation-0"
              :item-class="submissionRowClass"
            >
              <template v-slot:item.index="{ item }">
                {{ filteredSubmissions.indexOf(item) + 1 }}
              </template>
              <template v-slot:item.college_name="{ item }">
                <div>
                  <span class="font-weight-bold">{{ item.college_name || item.submission_college_code }}</span>
                </div>
                <div class="text-caption grey--text">{{ item.submission_college_code }}</div>
              </template>
              <template v-slot:item.submission_status="{ item }">
                <v-chip
                  x-small
                  :color="statusColor(item.submission_status)"
                  dark
                >
                  <v-icon x-small left>{{ statusIcon(item.submission_status) }}</v-icon>
                  {{ statusLabel(item.submission_status) }}
                </v-chip>
              </template>
              <template v-slot:item.submission_count_total="{ item }">
                <span v-if="item.submission_count_total > 0" class="font-weight-bold">
                  {{ item.submission_count_total }}
                </span>
                <span v-else class="grey--text">-</span>
              </template>
              <template v-slot:item.submission_confirmed_at="{ item }">
                <span v-if="item.submission_confirmed_at">{{ formatThaiDateShort(item.submission_confirmed_at) }}</span>
                <span v-else class="grey--text">-</span>
              </template>
              <template v-slot:item.submission_uploaded_at="{ item }">
                <span v-if="item.submission_uploaded_at">{{ formatThaiDateShort(item.submission_uploaded_at) }}</span>
                <span v-else class="grey--text">-</span>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-tooltip bottom v-if="item.submission_status === 'uploaded'">
                  <template v-slot:activator="{ on }">
                    <v-btn icon x-small color="green" v-on="on" @click="approveSubmission(item)" class="mr-1">
                      <v-icon small>mdi-check-circle</v-icon>
                    </v-btn>
                  </template>
                  <span>อนุมัติ</span>
                </v-tooltip>
                <v-tooltip bottom v-if="['uploaded','certified','pdf_generated','confirmed'].includes(item.submission_status)">
                  <template v-slot:activator="{ on }">
                    <v-btn icon x-small color="orange" v-on="on" @click="openRejectDialog(item)" class="mr-1">
                      <v-icon small>mdi-undo-variant</v-icon>
                    </v-btn>
                  </template>
                  <span>ส่งกลับแก้ไข</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn icon x-small color="indigo" v-on="on" @click="viewSubmissionDetail(item)">
                      <v-icon small>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>ดูรายละเอียด</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <!-- ============================================================ -->
        <!-- ส่วนที่ 4: Log ล่าสุด                                        -->
        <!-- ============================================================ -->
        <v-card outlined>
          <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
            <v-icon left color="brown">mdi-history</v-icon>
            ประวัติการดำเนินการล่าสุด
            <v-spacer></v-spacer>
            <v-btn icon small @click="loadLogs()" :loading="logsLoading">
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-data-table
            :headers="logHeaders"
            :items="recentLogs"
            :items-per-page="10"
            :loading="logsLoading"
            dense
            class="elevation-0"
            no-data-text="ไม่มีประวัติ"
          >
            <template v-slot:item.log_action="{ item }">
              <v-chip x-small :color="logActionColor(item.log_action)" dark>
                {{ logActionLabel(item.log_action) }}
              </v-chip>
            </template>
            <template v-slot:item.log_created_at="{ item }">
              {{ formatThaiDateTime(item.log_created_at) }}
            </template>
            <template v-slot:item.college_name="{ item }">
              {{ item.college_name || item.log_college_code || '-' }}
            </template>
          </v-data-table>
        </v-card>
      </base-material-card>

      <!-- ============================================================ -->
      <!-- Dialog: สร้างปีงบประมาณใหม่                                   -->
      <!-- ============================================================ -->
      <v-dialog v-model="createCycleDialog" persistent max-width="480">
        <v-card class="pa-5">
          <v-card-title class="text-h6 font-weight-bold justify-center pb-4">
            <v-icon left color="primary" large>mdi-calendar-plus</v-icon>
            สร้างปีงบประมาณใหม่
          </v-card-title>
          <v-card-text>
            <v-form ref="createCycleForm" lazy-validation>
              <v-text-field
                v-model="newFiscalYear"
                label="ปีงบประมาณ (พ.ศ.)"
                placeholder="เช่น 2569"
                outlined
                type="number"
                :rules="[v => !!v || 'กรุณากรอก', v => /^25\d{2}$/.test(v) || 'รูปแบบไม่ถูกต้อง']"
                hint="ระบบจะสร้าง 4 รอบ (ต.ค.-ธ.ค. / ม.ค.-มี.ค. / เม.ย.-มิ.ย. / ก.ค.-ก.ย.) อัตโนมัติ"
                persistent-hint
              ></v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions class="justify-center">
            <v-btn outlined rounded @click="createCycleDialog = false" class="mr-3">
              <v-icon left small>mdi-close</v-icon>ยกเลิก
            </v-btn>
            <v-btn
              color="primary"
              dark
              rounded
              depressed
              :loading="createCycleLoading"
              @click="createCycle()"
            >
              <v-icon left small>mdi-check</v-icon>สร้างปีงบประมาณ
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ============================================================ -->
      <!-- Dialog: แก้ไขวันที่รอบ                                        -->
      <!-- ============================================================ -->
      <v-dialog v-model="editRoundDialog" persistent max-width="600">
        <v-card class="pa-5">
          <v-card-title class="text-h6 font-weight-bold pb-3">
            <v-icon left color="warning">mdi-calendar-edit</v-icon>
            แก้ไขวันที่ — {{ editingRound.round_name }}
          </v-card-title>
          <v-card-text>
            <v-form ref="editRoundForm" lazy-validation>
              <v-row>
                <v-col cols="6">
                  <v-menu v-model="menuSD" :close-on-content-click="false" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="editingRound.round_start_date"
                        label="วันเปิดรับ"
                        outlined
                        dense
                        readonly
                        prepend-inner-icon="mdi-calendar"
                        v-bind="attrs"
                        v-on="on"
                        :rules="[v => !!v || 'กรุณาระบุ']"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="editingRound.round_start_date" @input="menuSD = false" locale="th"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6">
                  <v-menu v-model="menuED" :close-on-content-click="false" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="editingRound.round_end_date"
                        label="วันปิดรับ"
                        outlined
                        dense
                        readonly
                        prepend-inner-icon="mdi-calendar"
                        v-bind="attrs"
                        v-on="on"
                        :rules="[v => !!v || 'กรุณาระบุ']"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="editingRound.round_end_date" @input="menuED = false" locale="th"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6">
                  <v-menu v-model="menuDPS" :close-on-content-click="false" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="editingRound.round_data_period_start"
                        label="ช่วงข้อมูลเริ่มต้น"
                        outlined
                        dense
                        readonly
                        prepend-inner-icon="mdi-calendar-range"
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="editingRound.round_data_period_start" @input="menuDPS = false" locale="th"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6">
                  <v-menu v-model="menuDPE" :close-on-content-click="false" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="editingRound.round_data_period_end"
                        label="ช่วงข้อมูลสิ้นสุด"
                        outlined
                        dense
                        readonly
                        prepend-inner-icon="mdi-calendar-range"
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="editingRound.round_data_period_end" @input="menuDPE = false" locale="th"></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions class="justify-center">
            <v-btn outlined rounded @click="editRoundDialog = false" class="mr-3">
              <v-icon left small>mdi-close</v-icon>ยกเลิก
            </v-btn>
            <v-btn color="warning" dark rounded depressed :loading="editRoundLoading" @click="saveRoundDates()">
              <v-icon left small>mdi-content-save</v-icon>บันทึก
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ============================================================ -->
      <!-- Dialog: ดูรายละเอียด submission                               -->
      <!-- ============================================================ -->
      <v-dialog v-model="detailDialog" persistent max-width="90%" scrollable>
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            <v-icon left color="indigo">mdi-account-details</v-icon>
            รายละเอียด — {{ detailCollege.college_name }}
            <v-spacer></v-spacer>
            <v-chip small :color="statusColor(detailCollege.submission_status)" dark class="mr-3">
              {{ statusLabel(detailCollege.submission_status) }}
            </v-chip>
            <v-btn icon @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text style="max-height:600px">
            <!-- สรุปจำนวน -->
            <v-row class="mb-3">
              <v-col v-for="(cnt, idx) in detailCountCards" :key="idx" cols="4" sm="3" md="2">
                <v-card :color="cnt.bg" class="pa-2 text-center" flat rounded="lg">
                  <div class="text-subtitle-1 font-weight-bold" :style="{color: cnt.numColor}">{{ cnt.count }}</div>
                  <div class="text-caption" :style="{color: cnt.labelColor}" style="line-height:1.1">{{ cnt.label }}</div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Timeline -->
            <v-simple-table dense class="mb-4">
              <thead>
                <tr>
                  <th>ขั้นตอน</th>
                  <th>วันที่</th>
                  <th>ผู้ดำเนินการ</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>ยืนยันข้อมูล</td>
                  <td>{{ detailCollege.submission_confirmed_at ? formatThaiDateTime(detailCollege.submission_confirmed_at) : '-' }}</td>
                  <td>{{ detailCollege.submission_confirmed_by || '-' }}</td>
                </tr>
                <tr>
                  <td>สร้าง PDF</td>
                  <td>{{ detailCollege.submission_pdf_generated_at ? formatThaiDateTime(detailCollege.submission_pdf_generated_at) : '-' }}</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>ผอ. รับรอง</td>
                  <td>{{ detailCollege.submission_certified_at ? formatThaiDateTime(detailCollege.submission_certified_at) : '-' }}</td>
                  <td>{{ detailCollege.submission_certified_by || '-' }}</td>
                </tr>
                <tr>
                  <td>อัปโหลดเอกสาร</td>
                  <td>{{ detailCollege.submission_uploaded_at ? formatThaiDateTime(detailCollege.submission_uploaded_at) : '-' }}</td>
                  <td>-</td>
                </tr>
                <tr v-if="detailCollege.submission_approved_at">
                  <td>Admin อนุมัติ</td>
                  <td>{{ formatThaiDateTime(detailCollege.submission_approved_at) }}</td>
                  <td>{{ detailCollege.submission_approved_by || '-' }}</td>
                </tr>
              </tbody>
            </v-simple-table>

            <!-- เหตุผลส่งกลับ (ถ้ามี) -->
            <v-alert
              v-if="detailCollege.submission_reject_reason"
              type="warning"
              dense
              text
              class="mb-3"
            >
              <strong>เหตุผลที่ส่งกลับ:</strong> {{ detailCollege.submission_reject_reason }}
            </v-alert>

            <!-- Snapshot data -->
            <div class="text-subtitle-2 font-weight-bold mb-2">
              <v-icon small left>mdi-database-eye</v-icon>
              ข้อมูล Snapshot ({{ detailSnapshots.length }} รายการ)
            </div>
            <v-text-field
              v-model="detailSnapshotSearch"
              append-icon="mdi-magnify"
              label="ค้นหา"
              single-line
              hide-details
              dense
              outlined
              class="mb-2"
            ></v-text-field>
            <v-data-table
              :headers="snapshotHeaders"
              :items="detailSnapshots"
              :search="detailSnapshotSearch"
              :items-per-page="15"
              dense
            >
              <template v-slot:item.snapshot_m_type="{ item }">
                <v-chip x-small :color="typeColor(item.snapshot_m_type)" dark>
                  {{ typeLabel(item.snapshot_m_type) }}
                </v-chip>
              </template>
              <template v-slot:item.fullname="{ item }">
                {{ item.snapshot_title }}{{ item.snapshot_fristname }} {{ item.snapshot_lastname }}
              </template>
              <template v-slot:item.snapshot_salary="{ item }">
                {{ item.snapshot_salary ? Number(item.snapshot_salary).toLocaleString() : '-' }}
              </template>
            </v-data-table>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              v-if="detailCollege.submission_status === 'uploaded'"
              color="green"
              dark
              depressed
              rounded
              @click="approveSubmission(detailCollege); detailDialog = false"
            >
              <v-icon left small>mdi-check-circle</v-icon>อนุมัติ
            </v-btn>
            <v-btn
              v-if="['uploaded','certified','pdf_generated','confirmed'].includes(detailCollege.submission_status)"
              color="orange"
              dark
              depressed
              rounded
              @click="openRejectDialog(detailCollege); detailDialog = false"
            >
              <v-icon left small>mdi-undo-variant</v-icon>ส่งกลับ
            </v-btn>
            <v-btn text rounded @click="detailDialog = false">ปิด</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ============================================================ -->
      <!-- Dialog: ส่งกลับแก้ไข                                          -->
      <!-- ============================================================ -->
      <v-dialog v-model="rejectDialog" persistent max-width="500">
        <v-card class="pa-5">
          <v-card-title class="text-h6 font-weight-bold justify-center">
            <v-icon left color="orange" large>mdi-undo-variant</v-icon>
            ส่งกลับแก้ไข
          </v-card-title>
          <v-card-text>
            <div class="text-body-2 mb-3">
              สถานศึกษา: <strong>{{ rejectingSubmission.college_name }}</strong>
            </div>
            <v-textarea
              v-model="rejectReason"
              label="เหตุผลที่ส่งกลับ"
              outlined
              rows="3"
              :rules="[v => !!v || 'กรุณาระบุเหตุผล']"
            ></v-textarea>
          </v-card-text>
          <v-card-actions class="justify-center">
            <v-btn outlined rounded @click="rejectDialog = false" class="mr-3">ยกเลิก</v-btn>
            <v-btn
              color="orange"
              dark
              rounded
              depressed
              :loading="rejectLoading"
              :disabled="!rejectReason"
              @click="rejectSubmission()"
            >
              <v-icon left small>mdi-undo-variant</v-icon>ยืนยันส่งกลับ
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ============================================================ -->
      <!-- Dialog: ยืนยันเปิด/ปิดรอบ                                     -->
      <!-- ============================================================ -->
      <v-dialog v-model="actionDialog" persistent max-width="480">
        <v-card class="pa-5">
          <v-card-title class="text-h6 font-weight-bold justify-center">
            <v-icon left :color="actionData.color" large>{{ actionData.icon }}</v-icon>
            {{ actionData.title }}
          </v-card-title>
          <v-card-text class="text-center text-body-1 py-4">{{ actionData.message }}</v-card-text>
          <v-card-actions class="justify-center">
            <v-btn outlined rounded @click="actionDialog = false" class="mr-3">ยกเลิก</v-btn>
            <v-btn :color="actionData.color" dark rounded depressed :loading="actionLoading" @click="actionData.fn()">
              <v-icon left small>{{ actionData.icon }}</v-icon>{{ actionData.btnText }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top multi-line>
        <v-icon left>{{ snackbar.icon }}</v-icon>{{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn dark text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
export default {
  name: "PersonnelReportAdmin",

  data() {
    return {
      ApiKey: "HRvec2021",
      user: {},

      // Overview
      overviewStats: {},

      // Cycles & Rounds
      cycles: [],
      selectedCycleYear: null,
      rounds: [],
      activeRound: null,

      // Create cycle
      createCycleDialog: false,
      createCycleLoading: false,
      newFiscalYear: "",

      // Edit round dates
      editRoundDialog: false,
      editRoundLoading: false,
      editingRound: {},
      menuSD: false, menuED: false, menuDPS: false, menuDPE: false,

      // Submissions
      selectedRound: null,
      submissions: [],
      submissionsLoading: false,
      submissionSearch: "",
      submissionFilter: null,

      // Detail dialog
      detailDialog: false,
      detailCollege: {},
      detailSnapshots: [],
      detailSnapshotSearch: "",

      // Reject dialog
      rejectDialog: false,
      rejectingSubmission: {},
      rejectReason: "",
      rejectLoading: false,

      // Action dialog (open/close round)
      actionDialog: false,
      actionLoading: false,
      actionData: { title: "", message: "", icon: "", color: "", btnText: "", fn: () => {} },

      // Logs
      recentLogs: [],
      logsLoading: false,

      // Snackbar
      snackbar: { show: false, color: "", icon: "", text: "" },

      // Table headers
      submissionHeaders: [
        { text: "#", value: "index", width: "50px", sortable: false },
        { text: "สถานศึกษา", value: "college_name", width: "250px" },
        { text: "สถานะ", value: "submission_status", width: "150px" },
        { text: "บุคลากร", value: "submission_count_total", width: "100px", align: "center" },
        { text: "วันที่ยืนยัน", value: "submission_confirmed_at", width: "130px" },
        { text: "วันที่อัปโหลด", value: "submission_uploaded_at", width: "130px" },
        { text: "จัดการ", value: "actions", width: "130px", sortable: false, align: "center" }
      ],
      snapshotHeaders: [
        { text: "ประเภท", value: "snapshot_m_type", width: "150px" },
        { text: "ชื่อ-นามสกุล", value: "fullname", width: "220px" },
        { text: "เลขที่ตำแหน่ง", value: "snapshot_id_position", width: "110px" },
        { text: "อันดับ", value: "snapshot_level", width: "90px" },
        { text: "วิทยฐานะ", value: "snapshot_rang", width: "110px" },
        { text: "เงินเดือน", value: "snapshot_salary", width: "110px", align: "end" },
        { text: "ประเภทวิชา", value: "subject_name", width: "130px" }
      ],
      logHeaders: [
        { text: "วันเวลา", value: "log_created_at", width: "160px" },
        { text: "การดำเนินการ", value: "log_action", width: "140px" },
        { text: "สถานศึกษา", value: "college_name", width: "200px" },
        { text: "รายละเอียด", value: "log_detail" },
        { text: "ผู้ดำเนินการ", value: "log_user_account", width: "130px" }
      ],

      // Maps
      statusMap: {
        pending: { label: "รอดำเนินการ", color: "grey", icon: "mdi-clock-outline" },
        editing: { label: "กำลังแก้ไข", color: "orange", icon: "mdi-pencil" },
        confirmed: { label: "ยืนยันแล้ว", color: "blue", icon: "mdi-check" },
        pdf_generated: { label: "สร้าง PDF แล้ว", color: "deep-purple", icon: "mdi-file-pdf-box" },
        certified: { label: "ผอ.รับรอง", color: "amber darken-2", icon: "mdi-seal" },
        uploaded: { label: "อัปโหลดแล้ว", color: "red darken-1", icon: "mdi-cloud-check" },
        approved: { label: "อนุมัติ", color: "green", icon: "mdi-check-all" }
      },
      typeMap: {
        director: { label: "ผู้อำนวยการ", color: "indigo" },
        se_director: { label: "รอง ผอ.", color: "blue" },
        teacher: { label: "ข้าราชการครู", color: "teal" },
        supervision: { label: "ศึกษานิเทศก์", color: "cyan" },
        "38": { label: "38 ค.(2)", color: "blue-grey" },
        permanent: { label: "ลูกจ้างประจำ", color: "brown" },
        governmentteach: { label: "พนง.ราชการ(สอน)", color: "orange darken-2" },
        governmentsp: { label: "พนง.ราชการ(สนับสนุน)", color: "deep-orange" },
        temporary: { label: "ครูอัตราจ้าง", color: "purple" },
        temporary_job: { label: "ลูกจ้างชั่วคราว", color: "pink" }
      },
      logActionMap: {
        open_round: { label: "เปิดรอบ", color: "teal" },
        close_round: { label: "ปิดรอบ", color: "red" },
        confirm_data: { label: "ยืนยันข้อมูล", color: "blue" },
        generate_pdf: { label: "สร้าง PDF", color: "deep-purple" },
        upload_signed_pdf: { label: "อัปโหลด", color: "orange" },
        upload_generated_pdf: { label: "สร้าง PDF", color: "deep-purple" },
        approve: { label: "อนุมัติ", color: "green" },
        reject: { label: "ส่งกลับ", color: "orange darken-2" },
        create_cycle: { label: "สร้างปีงบ", color: "indigo" },
        certify: { label: "รับรอง", color: "amber darken-2" }
      },

      statusFilterOptions: [
        { text: "ทั้งหมด", value: null },
        { text: "รอดำเนินการ", value: "pending" },
        { text: "กำลังแก้ไข", value: "editing" },
        { text: "ยืนยันแล้ว", value: "confirmed" },
        { text: "สร้าง PDF แล้ว", value: "pdf_generated" },
        { text: "ผอ.รับรอง", value: "certified" },
        { text: "อัปโหลดแล้ว", value: "uploaded" },
        { text: "อนุมัติ", value: "approved" }
      ]
    };
  },

  computed: {
    cycleYearOptions() {
      return this.cycles.map(c => ({ text: "ปีงบประมาณ " + c.cycle_fiscal_year, value: c.cycle_fiscal_year }));
    },

    filteredSubmissions() {
      if (!this.submissionFilter) return this.submissions;
      return this.submissions.filter(s => s.submission_status === this.submissionFilter);
    },

    uploadedCount() {
      return this.submissions.filter(s => s.submission_status === "uploaded").length;
    },

    summaryStatusCards() {
      const counts = {};
      this.submissions.forEach(s => {
        counts[s.submission_status] = (counts[s.submission_status] || 0) + 1;
      });
      return [
        { label: "รอดำเนินการ", value: "pending", count: counts.pending || 0, bg: "#F1EFE8", numColor: "#5F5E5A", labelColor: "#888780" },
        { label: "กำลังแก้ไข", value: "editing", count: counts.editing || 0, bg: "#FAEEDA", numColor: "#854F0B", labelColor: "#BA7517" },
        { label: "ยืนยันแล้ว", value: "confirmed", count: counts.confirmed || 0, bg: "#E6F1FB", numColor: "#0C447C", labelColor: "#185FA5" },
        { label: "สร้าง PDF", value: "pdf_generated", count: counts.pdf_generated || 0, bg: "#EEEDFE", numColor: "#3C3489", labelColor: "#534AB7" },
        { label: "ผอ.รับรอง", value: "certified", count: counts.certified || 0, bg: "#FAEEDA", numColor: "#633806", labelColor: "#854F0B" },
        { label: "อัปโหลดแล้ว", value: "uploaded", count: counts.uploaded || 0, bg: "#FCEBEB", numColor: "#791F1F", labelColor: "#A32D2D" },
        { label: "อนุมัติ", value: "approved", count: counts.approved || 0, bg: "#EAF3DE", numColor: "#27500A", labelColor: "#3B6D11" }
      ];
    },

    overallProgressPercent() {
      if (this.submissions.length === 0) return 0;
      const completed = this.submissions.filter(s => ["uploaded", "approved"].includes(s.submission_status)).length;
      return Math.round((completed / this.submissions.length) * 100);
    },

    overallProgressColor() {
      if (this.overallProgressPercent >= 80) return "green";
      if (this.overallProgressPercent >= 50) return "blue";
      if (this.overallProgressPercent >= 20) return "orange";
      return "red";
    },

    detailCountCards() {
      const d = this.detailCollege;
      return [
        { label: "ผอ.", count: d.submission_count_directors || 0, bg: "#E6F1FB", numColor: "#0C447C", labelColor: "#185FA5" },
        { label: "รอง ผอ.", count: d.submission_count_se_directors || 0, bg: "#E6F1FB", numColor: "#0C447C", labelColor: "#185FA5" },
        { label: "ครู", count: d.submission_count_teachers || 0, bg: "#E1F5EE", numColor: "#085041", labelColor: "#0F6E56" },
        { label: "พนง.ราชการ(สอน)", count: d.submission_count_governmentteach || 0, bg: "#FAEEDA", numColor: "#633806", labelColor: "#854F0B" },
        { label: "ครูอัตราจ้าง", count: d.submission_count_temporarys || 0, bg: "#EEEDFE", numColor: "#3C3489", labelColor: "#534AB7" },
        { label: "รวม", count: d.submission_count_total || 0, bg: "#2C2C2A", numColor: "#fff", labelColor: "#D3D1C7" }
      ];
    }
  },

  async mounted() {
    let userSession = JSON.parse(sessionStorage.getItem("user")) || {};
    let result = await this.$http.post("user.php", { ApiKey: this.ApiKey, user_ID: userSession.user_ID });
    this.user = result.data;
    await Promise.all([this.loadOverview(), this.loadCycles(), this.loadActiveRound(), this.loadLogs()]);
    if (this.cycles.length > 0 && !this.selectedCycleYear) {
      this.selectedCycleYear = this.cycles[0].cycle_fiscal_year;
      await this.loadRounds();
    }
  },

  methods: {
    // ========== Helpers ==========
    statusLabel(s) { return (this.statusMap[s] || {}).label || s; },
    statusColor(s) { return (this.statusMap[s] || {}).color || "grey"; },
    statusIcon(s) { return (this.statusMap[s] || {}).icon || "mdi-help"; },
    typeLabel(t) { return (this.typeMap[t] || {}).label || t; },
    typeColor(t) { return (this.typeMap[t] || {}).color || "grey"; },
    logActionLabel(a) { return (this.logActionMap[a] || {}).label || a; },
    logActionColor(a) { return (this.logActionMap[a] || {}).color || "grey"; },
    roundStatusColor(s) {
      return { draft: "grey", open: "teal", closing: "orange", closed: "red darken-1" }[s] || "grey";
    },
    roundStatusLabel(s) {
      return { draft: "ร่าง", open: "เปิดอยู่", closing: "กำลังปิด", closed: "ปิดแล้ว" }[s] || s;
    },
    formatThaiDateShort(d) {
      if (!d) return "-";
      const months = ["","ม.ค.","ก.พ.","มี.ค.","เม.ย.","พ.ค.","มิ.ย.","ก.ค.","ส.ค.","ก.ย.","ต.ค.","พ.ย.","ธ.ค."];
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return d;
      return dt.getDate() + " " + months[dt.getMonth()+1] + " " + (dt.getFullYear()+543);
    },
    formatThaiDateTime(d) {
      if (!d) return "-";
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return d;
      return this.formatThaiDateShort(d) + " " + String(dt.getHours()).padStart(2,"0") + ":" + String(dt.getMinutes()).padStart(2,"0");
    },
    showSnack(color, icon, text) { this.snackbar = { show: true, color, icon, text }; },
    filterByStatus(val) { this.submissionFilter = this.submissionFilter === val ? null : val; },
    submissionRowClass(item) {
      if (item.submission_status === "approved") return "green lighten-5";
      if (item.submission_status === "uploaded") return "orange lighten-5";
      if (item.submission_status === "pending") return "";
      return "";
    },

    // ========== Data Loading ==========
    async loadOverview() {
      try {
        let r = await this.$http.post("personnel_report_cycle.php", { ApiKey: this.ApiKey, action: "overview_stats" });
        this.overviewStats = r.data || {};
      } catch (e) { this.overviewStats = {}; }
    },
    async loadCycles() {
      try {
        let r = await this.$http.post("personnel_report_cycle.php", { ApiKey: this.ApiKey, action: "list_cycles" });
        this.cycles = Array.isArray(r.data) ? r.data : [];
      } catch (e) { this.cycles = []; }
    },
    async loadActiveRound() {
      try {
        let r = await this.$http.post("personnel_report_round.php", { ApiKey: this.ApiKey, action: "get_active_round" });
        this.activeRound = (r.data && r.data.round_id) ? r.data : null;
      } catch (e) { this.activeRound = null; }
    },
    async loadRounds() {
      if (!this.selectedCycleYear) { this.rounds = []; return; }
      try {
        let r = await this.$http.post("personnel_report_round.php", { ApiKey: this.ApiKey, action: "list_rounds", fiscal_year: this.selectedCycleYear });
        this.rounds = Array.isArray(r.data) ? r.data : [];
      } catch (e) { this.rounds = []; }
    },
    async loadRoundSubmissions(roundId) {
      this.submissionsLoading = true;
      try {
        let r = await this.$http.post("personnel_report_round.php", { ApiKey: this.ApiKey, action: "round_submissions", round_id: roundId });
        this.submissions = Array.isArray(r.data) ? r.data : [];
      } catch (e) { this.submissions = []; }
      finally { this.submissionsLoading = false; }
    },
    async loadLogs() {
      this.logsLoading = true;
      try {
        let r = await this.$http.post("personnel_report_cycle.php", { ApiKey: this.ApiKey, action: "recent_logs", limit: 50 });
        this.recentLogs = Array.isArray(r.data) ? r.data : [];
      } catch (e) { this.recentLogs = []; }
      finally { this.logsLoading = false; }
    },

    // ========== Actions ==========
    viewRoundSubmissions(round) {
      this.selectedRound = round;
      this.submissionFilter = null;
      this.submissionSearch = "";
      this.loadRoundSubmissions(round.round_id);
    },

    openCreateCycleDialog() {
      const currentYear = new Date().getFullYear() + 544;
      this.newFiscalYear = String(currentYear);
      this.createCycleDialog = true;
    },

    async createCycle() {
      if (!this.$refs.createCycleForm.validate()) return;
      this.createCycleLoading = true;
      try {
        let r = await this.$http.post("personnel_report_cycle.php", {
          ApiKey: this.ApiKey, action: "create_cycle",
          fiscal_year: this.newFiscalYear, user_account: this.user.user_name
        });
        if (r.data.status) {
          this.showSnack("success", "mdi-check-circle", r.data.message);
          this.createCycleDialog = false;
          await this.loadCycles();
          this.selectedCycleYear = this.newFiscalYear;
          await this.loadRounds();
        } else {
          this.showSnack("warning", "mdi-alert", r.data.message);
        }
      } catch (e) { this.showSnack("error", "mdi-alert-circle", e.message); }
      finally { this.createCycleLoading = false; }
    },

    openEditRoundDialog(round) {
      this.editingRound = { ...round };
      this.editRoundDialog = true;
    },

    async saveRoundDates() {
      if (!this.$refs.editRoundForm.validate()) return;
      this.editRoundLoading = true;
      try {
        let r = await this.$http.post("personnel_report_cycle.php", {
          ApiKey: this.ApiKey, action: "update_round_dates",
          round_id: this.editingRound.round_id,
          round_start_date: this.editingRound.round_start_date,
          round_end_date: this.editingRound.round_end_date,
          round_data_period_start: this.editingRound.round_data_period_start,
          round_data_period_end: this.editingRound.round_data_period_end
        });
        if (r.data.status) {
          this.showSnack("success", "mdi-check-circle", r.data.message);
          this.editRoundDialog = false;
          await this.loadRounds();
        } else {
          this.showSnack("warning", "mdi-alert", r.data.message);
        }
      } catch (e) { this.showSnack("error", "mdi-alert-circle", e.message); }
      finally { this.editRoundLoading = false; }
    },

    confirmAction(type, round) {
      if (type === "open") {
        this.actionData = {
          title: "เปิดรอบรายงาน", icon: "mdi-lock-open-variant", color: "teal", btnText: "ยืนยันเปิดรอบ",
          message: `ยืนยันเปิด "${round.round_name}" ? ระบบจะสร้าง submission สำหรับทุกสถานศึกษา`,
          fn: () => this.doOpenRound(round.round_id)
        };
      } else {
        this.actionData = {
          title: "ปิดรอบรายงาน", icon: "mdi-lock", color: "red darken-1", btnText: "ยืนยันปิดรอบ",
          message: `ยืนยันปิด "${round.round_name}" ? สถานศึกษาจะไม่สามารถรายงานข้อมูลรอบนี้ได้อีก`,
          fn: () => this.doCloseRound(round.round_id)
        };
      }
      this.actionDialog = true;
    },

    async doOpenRound(roundId) {
      this.actionLoading = true;
      try {
        let r = await this.$http.post("personnel_report_round.php", {
          ApiKey: this.ApiKey, action: "open_round", round_id: roundId, user_account: this.user.user_name
        });
        if (r.data.status) {
          this.showSnack("success", "mdi-check-circle", r.data.message);
          this.actionDialog = false;
          await Promise.all([this.loadRounds(), this.loadActiveRound(), this.loadOverview(), this.loadLogs()]);
        } else { this.showSnack("warning", "mdi-alert", r.data.message); }
      } catch (e) { this.showSnack("error", "mdi-alert-circle", e.message); }
      finally { this.actionLoading = false; }
    },

    async doCloseRound(roundId) {
      this.actionLoading = true;
      try {
        let r = await this.$http.post("personnel_report_round.php", {
          ApiKey: this.ApiKey, action: "close_round", round_id: roundId, user_account: this.user.user_name
        });
        if (r.data.status) {
          this.showSnack("success", "mdi-check-circle", r.data.message);
          this.actionDialog = false;
          await Promise.all([this.loadRounds(), this.loadActiveRound(), this.loadOverview(), this.loadLogs()]);
          if (this.selectedRound && this.selectedRound.round_id === roundId) {
            this.loadRoundSubmissions(roundId);
          }
        } else { this.showSnack("warning", "mdi-alert", r.data.message); }
      } catch (e) { this.showSnack("error", "mdi-alert-circle", e.message); }
      finally { this.actionLoading = false; }
    },

    async approveSubmission(item) {
      try {
        let r = await this.$http.post("personnel_report_cycle.php", {
          ApiKey: this.ApiKey, action: "approve_submission",
          submission_id: item.submission_id, user_account: this.user.user_name
        });
        if (r.data.status) {
          this.showSnack("success", "mdi-check-circle", r.data.message);
          if (this.selectedRound) this.loadRoundSubmissions(this.selectedRound.round_id);
          this.loadOverview(); this.loadLogs();
        } else { this.showSnack("warning", "mdi-alert", r.data.message); }
      } catch (e) { this.showSnack("error", "mdi-alert-circle", e.message); }
    },

    async approveAllUploaded() {
      const uploaded = this.submissions.filter(s => s.submission_status === "uploaded");
      if (uploaded.length === 0) return;
      for (const item of uploaded) {
        await this.approveSubmission(item);
      }
    },

    openRejectDialog(item) {
      this.rejectingSubmission = item;
      this.rejectReason = "";
      this.rejectDialog = true;
    },

    async rejectSubmission() {
      if (!this.rejectReason) return;
      this.rejectLoading = true;
      try {
        let r = await this.$http.post("personnel_report_cycle.php", {
          ApiKey: this.ApiKey, action: "reject_submission",
          submission_id: this.rejectingSubmission.submission_id,
          reason: this.rejectReason, user_account: this.user.user_name
        });
        if (r.data.status) {
          this.showSnack("success", "mdi-check-circle", r.data.message);
          this.rejectDialog = false;
          if (this.selectedRound) this.loadRoundSubmissions(this.selectedRound.round_id);
          this.loadOverview(); this.loadLogs();
        } else { this.showSnack("warning", "mdi-alert", r.data.message); }
      } catch (e) { this.showSnack("error", "mdi-alert-circle", e.message); }
      finally { this.rejectLoading = false; }
    },

    async viewSubmissionDetail(item) {
      this.detailCollege = item;
      this.detailSnapshots = [];
      this.detailSnapshotSearch = "";
      this.detailDialog = true;
      try {
        let r = await this.$http.post("personnel_report_submission.php", {
          ApiKey: this.ApiKey, action: "get_snapshot", submission_id: item.submission_id
        });
        this.detailSnapshots = Array.isArray(r.data) ? r.data : [];
      } catch (e) { this.detailSnapshots = []; }
    }
  }
};
</script>

<style scoped>
.round-card {
  transition: all 0.3s ease;
  border-width: 1px;
}
.round-card:hover {
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}
.round-card-open {
  border-color: #009688 !important;
  border-width: 2px !important;
  background: rgba(0,150,136,0.03);
}
.round-card-closed {
  opacity: 0.7;
}
.summary-status-card {
  transition: all 0.2s ease;
}
.summary-status-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}
</style>